import { action, computed, makeObservable, observable } from 'mobx';
import { SERVER } from '@/request/config';
import { httpRequest } from '@/request';
import { formatProductName, isMobile, cache, querySelector } from '@/utils';
import { enableStaticRendering } from 'mobx-react';
import { getBatteryOrChargeHubName } from '@/utils/configs/product.config';
import { GlobalTracker, SearchTracker } from '@/utils/Tracker';
import {
  navStyleConfig,
  customMasterConfig,
  getExtraNavInfo,
  customCidMasterConfig,
} from '@/components/Header/Nav/config.data';
import StoreBase from './Base';
import { checkIsSearchResultsPage } from 'src/pages/search/utils';
import { armsErrorSend } from '@/utils/libs/armsErrorSend';
import { ENavState } from './types/meta';

enableStaticRendering(typeof window === 'undefined');

/** cart使用场景 */
export const CartUsePositions = {
  /** 导航栏（默认激活场景） */
  navBar: 'nav-bar',
  /** 配件推荐页 */
  relatedAccessoriesPage: 'related-accessories-page',
  /** 搜索页 */
  searchPage: 'search-page',
};

class State extends StoreBase {
  constructor(props) {
    super(props);
    makeObservable(this);
  }

  // 包邮门槛数据
  @observable
  freeShippingAmount = {};

  // 直播信息
  @observable
  liveInfo = null;

  @observable
  LiveUrlEmbed = '';

  @observable
  LiveClosed = false;

  @observable
  LiveFullScreen = false;

  @observable
  SmallScreenVisible = false;

  /** 当前激活的购物车模块位置，可用于页面存在多个cart时，控制只有单个cart生效（加车时显示购物车列表） */
  @observable
  currentActiveCartPosition = CartUsePositions.navBar;

  // topBar 轮播信息
  @observable
  topBarInfos = [];

  // 包邮信息 从topbar列表取，id=7
  @observable
  freeShippingTip = '';

  // 导航栏配置
  @observable
  navigationBarInfos = [];

  @observable
  listNavigationBarInfos = {};

  // 移动端浮动购物车显示状态
  @observable
  popupCartListVisible = false;

  popupCartListVisibleTimer = null;

  @observable
  navStateInfos = {
    is2024MTCSalePage: false, // 是否为2024MTC活动页
    is2025TravelSalePage: false, // s是否为Travel垂类活动页
    isCampaignPage: false, // 是否为common活动页（自定义navbar）
    isNavFixed: false, // 导航栏是否吸顶
    isHomePage: false, // 用于判断当前页是否为首页
    isHover: false, // 鼠标是否悬停
    isMobileNavOpen: false, // 移动端导航打开状态
    curState: 0, // 当前导航栏状态
  };

  @action
  updateCurrentActiveCartPosition = (position = CartUsePositions.navBar) => {
    this.currentActiveCartPosition = position;
  };

  // 当前的导航栏是透明文字模式，hover时的展示逻辑需要特殊处理
  @computed
  get isNavTextTransparent() {
    return [ENavState.DarkTransparentFull, ENavState.LightTransparentFull].includes(this.navStateInfos.curState);
  }

  @computed
  get navCurStyle() {
    const { isMobile } = this.root.app;
    if (this.navStateInfos.isMobileNavOpen) return navStyleConfig['default'];
    if (this.navStateInfos.isHover) return navStyleConfig['default'];

    // todo: 活动变量聚合成一个
    if (!this.navStateInfos.is2024MTCSalePage && !this.navStateInfos.isCampaignPage) {
      if (!this.navStateInfos.isHomePage) return navStyleConfig['default'];
      if (!this.navStateInfos.isNavFixed) return navStyleConfig['default'];
    }

    if (this.navStateInfos.is2025TravelSalePage) {
      if (isMobile) return navStyleConfig['default'];
      return navStyleConfig['lightTransparentFull'];
    }

    if (this.navStateInfos.is2024MTCSalePage) {
      if (isMobile && !this.navStateInfos.isNavFixed) return navStyleConfig['default'];
      return navStyleConfig['light'];
    }

    switch (this.navStateInfos.curState) {
      case 0:
        return navStyleConfig['default'];
      case 1:
        return navStyleConfig['dark'];
      case 2:
        return navStyleConfig['light'];
      case 3:
        return navStyleConfig['darkTransparentFull'];
      case 4:
        return navStyleConfig['lightTransparentFull'];
      default:
        return navStyleConfig['default'];
    }
  }

  @action
  getLiveUrl = () => {
    httpRequest({
      url: SERVER.API_SERVER_META + '/getLiveLink',
    }).then(({ data }) => {
      if (!data.live_link) {
        this.handleLive({ action: 'close' });
        return;
      }
      this.handleLive({
        action: 'set',
        embedUrl: data.live_link,
      });
    });
  };

  // 处理直播相关
  @action
  handleLive = ({ action, embedUrl, from }) => {
    switch (action) {
      case 'set':
        this.LiveUrlEmbed = embedUrl;
        return;
      case 'get':
        this.LiveClosed = cache.get(`fb_live_close_${this.liveInfo?.id}`) === `1`;
        if (!this.LiveClosed) {
          this.changePageToolBarPosition(!isMobile() ? 290 : 200);
        }
        return;
      case 'narrow':
        this.LiveFullScreen = false;
        return;
      case 'enterFullScreen':
        this.LiveFullScreen = true;
        this.LiveClosed = false;
        return;
      case 'showEmbed':
        this.LiveFullScreen = false;
        this.LiveClosed = false;
        this.SmallScreenVisible = true;
        this.changePageToolBarPosition(!isMobile() ? 290 : 200);
        GlobalTracker.FBLive({ action: 'live_play', eventLabel: from });
        return;
      case 'close':
        this.LiveFullScreen = false;
        this.LiveClosed = true;
        this.SmallScreenVisible = false;
        this.changePageToolBarPosition();
        cache.set(`fb_live_close_${this.liveInfo?.id}`, 1);
        GlobalTracker.FBLive({ action: 'live_close', eventLabel: from });
        break;
      default:
    }
  };

  /**
   * 调整底部toolBar的位置，interCom弹窗位置right 24，bottom为icon的底部
   * @param {Number} bottom 恢复默认的高度无需穿参。
   * @param {function} cb
   */
  @action
  changePageToolBarPosition = (bottom, cb) => {
    if (typeof window === 'undefined') return;
    const { G_IsProductPage, G_IsResellerIntro, isCartPage, isMobile } = this.root.app;
    const { eleStickyTabBarVisible } = this.root.product;
    const { cartStickyBarVisible } = this.root.order;
    const defaultBottom = 48;
    const defaultModalPadding = 24;

    let bottomPadding = bottom;
    let modalPadding = defaultModalPadding;
    // 不传参时，恢复默认设置的toolBar高度
    if (typeof bottomPadding !== 'number') {
      switch (true) {
        case G_IsProductPage:
          // eslint-disable-next-line no-case-declarations
          const pStickyBarHeight = this.root.product.eleStickyTabBar?.clientHeight ?? 0;
          bottomPadding = eleStickyTabBarVisible ? pStickyBarHeight + defaultBottom : defaultBottom;
          modalPadding = isMobile ? 0 : 80 + modalPadding;
          break;
        case G_IsResellerIntro && isMobile:
          bottomPadding = 60 + defaultBottom;
          break;
        case isCartPage && isMobile:
          // eslint-disable-next-line no-case-declarations
          const cartStickyBarHeight = this.root.order.cartStickyBarElement?.clientHeight ?? 0;
          bottomPadding = cartStickyBarVisible ? cartStickyBarHeight + defaultBottom : defaultBottom;
          break;
        default:
          bottomPadding = defaultBottom;
          break;
      }
    }

    // 聊天窗口固定距离 默认x:24,y:24
    window.intercomSettings = Object.assign({}, window.intercomSettings, {
      horizontal_padding: 24,
      vertical_padding: modalPadding,
    });
    window.Intercom?.('update', window.intercomSettings);

    querySelector('#fixedToolBar').then((dom) => {
      this.fixedToolBarElement = dom;
      dom.style.bottom = `${bottomPadding}px`;
    });
    cb?.(bottomPadding);
  };

  @action
  togglePopupCartListVisible = (visible, clickClose = false) => {
    clearTimeout(this.popupCartListVisibleTimer);
    this.popupCartListVisible = visible;
    if (visible) {
      if (isMobile()) {
        document.body.setAttribute('data-HideCustomerService', 'true');
      }
      GlobalTracker.navigation({ action: 'cart_pop_expose' });
      // 搜索结果页的购物车曝光
      if (checkIsSearchResultsPage()) {
        SearchTracker.searchResultsPageTracing({
          action: 'cart_pop_expose',
          label: 'search_result_page',
        });
      }

      this.popupCartListVisibleTimer = setTimeout(() => {
        this.togglePopupCartListVisible(false);
      }, 1500);
    } else {
      if (clickClose) {
        GlobalTracker.navigation({ action: 'cart_pop_close' });
      }
      if (isMobile()) {
        document.body.removeAttribute('data-HideCustomerService');
      }
    }
  };

  /**
   * 移动端购物车弹窗位置
   * @param {Number} top 弹窗距离顶部距离，不填参数为默认状态
   */
  @action
  changePopupCartListPosition = (top = undefined) => {
    if (top === undefined) {
      querySelector('#popupCartList').then((dom) => {
        if (dom && dom.style) {
          dom.style.removeProperty('position');
          dom.style.removeProperty('top');
        }
      });
      return;
    }
    querySelector('#popupCartList').then((dom) => {
      dom.style.position = 'fixed';
      dom.style.top = `${top}px`;
    });
  };

  // 卖点
  @observable
  tradePointInfos = [];

  @action
  setStateValue = (state, value) => {
    this[state] = value;
  };

  @action
  fetchNavigationBar = () => {
    let { commodityInfos = [], navigationBarInfos = [] } = this.listNavigationBarInfos;
    const { isTargetingResult } = this.root.abtest;
    const { LANG } = this.root.area;

    const isOriginAC = isTargetingResult('Accessory_activity_tags', 'original');

    // 从文案系统抓取套餐名替换
    commodityInfos.forEach((o) => {
      const SpecialProductName = LANG[`navigation.SpecialProductName.${o.commodityId}`];
      if (SpecialProductName) o.productName = SpecialProductName;
    });

    /**
     * 处理导航配置的超链接
     * @param {string} href 链接
     * @param {string} target 链接跳转方式，默认为新标签页
     */
    const handleUrlLink = (href = '', target = '_blank') => {
      const { isMobile } = this.root.app;
      const res = {
        href,
        target: isMobile ? '_self' : target,
      };
      if (!res.href) return res;
      if (isMobile) return res;

      // 商城链接内部跳转，其他外部跳转
      // 未填写域名默认为商城链接
      const hasDomain = /^http.+?/i.test(res.href);
      const isStoreUrlLink = /^http.+?\/\/store(.+)?\.insta360.com/i.test(res.href);

      res.target = hasDomain ? (isStoreUrlLink ? '_self' : '_blank') : '_self';

      return res;
    };

    // 处理导航栏产品名称
    const handleProduct = (c) => {
      const batteryOrChargeHubName = getBatteryOrChargeHubName({
        cid: c.commodityId,
        LANG: this.root.area.LANG,
      });
      c.productName = batteryOrChargeHubName || c.productName;
      c.productNameFormat = formatProductName(c.productName);
      return c;
    };

    // 处理产品套餐列表，过滤缺货、禁用、未配置产品主图
    commodityInfos = commodityInfos.filter((c) => {
      c = handleProduct(c);
      // // 大陆VR房产套餐安居客特殊配置
      // if (c.productId === 225 && ['INR5IM7', 'INRJZGZ'].includes(insrc)){
      //   const exclusiveProduct = [956, 957, 1204, 1205] // 专属套餐
      //   return exclusiveProduct.includes(c.commodityId)
      // }
      return c.saleState !== -2 && c.enabled && c.displayUrl;
    });

    // 把套餐信息添加到导航套餐列表
    navigationBarInfos.forEach((nav) => {
      const navExtraInfo = getExtraNavInfo({ isProd: this.root.app.isProd });

      if (navExtraInfo[nav?.id]) {
        nav.extraInfo = navExtraInfo[nav?.id];
      }

      nav?.categorySubsets?.forEach((subset) => {
        subset.navigationBarCommodityInfos?.forEach((item) => {
          const c = commodityInfos.filter((o) => o.commodityId === item.commodityId)?.[0] || {};
          // 根据套餐id维度自定义主图 优先级高于 产品id维度
          if (customCidMasterConfig[c?.commodityId]) {
            c.displayUrl = customCidMasterConfig[c?.commodityId]?.url;
          } else if (customMasterConfig[c?.productId]) {
            c.displayUrl = customMasterConfig[c?.productId]?.url;
          }
          item = Object.assign(item, c);
        });
        subset.navigationBarCommodityInfos = subset.navigationBarCommodityInfos?.filter((o) => o.productId);
      });
      nav.categorySubsets = nav?.categorySubsets?.filter((subset) => subset.navigationBarCommodityInfos?.length) ?? [];
      // 处理一级导航
      nav.formatHref = handleUrlLink(nav?.urlLink);

      // 对新配件导航进行处理 ———— 给套餐添加链接
      nav?.subsetList?.forEach((subset) => {
        if (subset.type === 'commodity') {
          subset?.thirdList?.forEach((o) => {
            const c = commodityInfos.find((i) => i.commodityId === o.commodityId) || {};
            o.urlLink = `/product/${c.productUrlKey}?c=${c.commodityId}`;
            o.thirdName = o?.thirdName?.replace(/Insta360|影石/g, '') ?? '';
          });
        }
      });
    });

    // 导航栏区分类型做逻辑处理
    navigationBarInfos = navigationBarInfos
      .filter((nav) => {
        const { isMobile } = this.root.app;
        switch (nav.type) {
          case 'textLink':
            if (!isMobile && !nav.isPc) return false;
            if (isMobile && !nav.isMobile) return false;
            return nav;
          case 'camera':
          case 'accessory':
          case 'service':
          case 'serviceNew':
            nav.isProductNav = true; // 标记为产品导航
            return nav.categorySubsets.length > 0; // 产品类型，过滤掉不存在二级导航的数据
          case 'accessoryNew':
          case 'accessory2':
            const categoryInfo = nav.subsetList?.filter((o) => o.type === 'category');
            const linkInfo = nav.subsetList?.find((o) => o.type === 'textLink');
            const commodityInfo = nav.subsetList?.find((o) => o.type === 'commodity');
            if (nav.type === 'accessory2') {
              categoryInfo?.forEach((o) => (o.type = 'commodity'));
            }
            nav.acList = [...(categoryInfo ?? [{}]), linkInfo ?? {}, commodityInfo ?? {}];

            nav.isProductNav = true; // 标记为产品导航
            return nav;
          case 'campaign':
          case 'recommendActivity':
            // console.log(nav);
            // 1. 二级导航为空，该导航默认为文字链，导航有效；
            if (!nav?.bannerMains?.length) {
              nav.isCampaignTextNav = true; // 标记为活动文字链导航
              return nav;
            }
            // 2. 二级导航不为空
            // 过滤banner不在有效期内的
            nav.bannerMains = nav.bannerMains.filter((banner) => {
              banner.bannerSubsets = banner.bannerSubsets.filter((o) => o.enabled); // 过滤未启用的
              banner.formatHref = handleUrlLink(banner.urlLink);
              const nowTime = new Date().getTime();
              const isValid =
                nowTime > banner.startTime && nowTime < banner.endTime && banner.enabled && banner.bannerSubsets.length;
              return isValid;
            });

            // 若banner均不在有效期内，则不显示该banner导航
            // 若banner部分有效，则根据设置规则，显示PC和Mobile导航
            if (nav.bannerMains.length) {
              nav.bannerMains = nav.bannerMains.filter((banner) => {
                return isMobile ? banner['isMobile'] : banner['isPc'];
              });
            }
            if (!nav.bannerMains.length) {
              // 推荐活动类型下，如果没有子项，那就直接过滤，不展示在一级导航
              if (nav.type === 'recommendActivity') return false;
              nav.isCampaignTextNav = true; // 标记为活动文字链导航
            } else {
              nav.isCampaignGroupNav = true; // 标记为活动群组导航
            }
            return nav;
          // case 'serviceNew':
          //   nav.categorySubsets.forEach((item) => {
          //     if(item.navigationBarCommodityInfos.length > 8){
          //        item.navigationBarCommodityInfos.splice(7);
          //        item.navigationBarCommodityInfos.push({
          //           productNameFormat: {
          //             simple: '查看更多'
          //           },
          //           displayUrl: 'https://res.insta360.com/static/59b4cefc72ac989f64f5f5ff43d13925/close.png',
          //           bannerInsideName:'查看更多服务',
          //           commodityId: 0,
          //           serviceUrl:'/service?from=nav'
          //        });
          //     }
          //   });
          //   nav.isProductNav = true; // 标记为产品导航
          //   return nav.categorySubsets.length > 0; // 产品类型，过滤掉不存在二级导航的数据
        }
        return nav;
      })
      .filter((o) => !!o)
      .filter((o) => o.type !== 'accessory')
      .filter((o) => (isOriginAC ? o.type !== 'accessory2' : o.type !== 'accessoryNew'));

    // 遍历 navigationBarInfos
    navigationBarInfos.forEach((navBarItem) => {
      // 找到 categoryName 为 "Consumer" 的项 和 “Professional”的项
      if (navBarItem.id === 76 || navBarItem.id === 77) {
        // 遍历该项的 categorySubsets
        navBarItem.categorySubsets.forEach((subsetItem) => {
          // 遍历 categorySubsets 中的 navigationBarCommodityInfos
          subsetItem.navigationBarCommodityInfos.forEach((commodityItem) => {
            // 对commodityItem.productNameFormat.simple二次处理
            commodityItem.productNameFormat.simple = commodityItem.productNameFormat?.name.replace('影石', '');
            if (['CN'].includes(this.root.area?.country?.code)) {
              commodityItem.productNameFormat.simple = commodityItem.productNameFormat.simple.replace('Insta360', '');
            }
          });
        });
      }
    });
    this.setStateValue('navigationBarInfos', navigationBarInfos);
  };

  @action
  fetchOtherListQuestion = ({ type, pageKey }) => {
    return httpRequest({
      url: SERVER.API_SERVER_BASE + '/faq/other/listQuestionBind',
      params: {
        type,
        pageKey,
      },
    })
      .then((res) => {
        if (res.code === 0 && res.data?.faqQuestionBinds?.questionQaBinds) {
          res.data.faqQuestionBinds.questionQaBinds.forEach((d) => (d.answerParse = JSON.parse(d.answer)));
        }

        return res;
      })
      .catch((e) => {
        armsErrorSend(e, { filename: 'orderMobx fetchOtherListQuestion' });
        return {};
      });
  };

  @action
  fetchAccessoryCategoryList = ({ defaultDisplay, imageTextSelectorId, categoryKey }) => {
    return httpRequest({
      url: SERVER.API_SERVER_BASE + '/meta/cp/listCategory',
      params: {
        defaultDisplay,
        imageTextSelectorId,
        categoryKey,
      },
    });
  };
}

export default State;
